// src/pages/Home.jsx
import React, { useEffect } from 'react';
import { useLocation, Link as RouterLink } from 'react-router-dom';
import { Box, Flex, Grid, HStack, Heading, Text, Link, Image, Wrap } from '@chakra-ui/react';
import { Helmet } from 'react-helmet-async';
import { ArrowRight } from 'lucide-react';
import { getAllProjects } from '../services/projects';
import Tag from '../components/Tag';

const EMAIL = 'elyzabethperez.s@gmail.com';

const LINKS = [
  { label: 'GitHub', href: 'https://github.com/MichellePerezS' },
  { label: 'LinkedIn', href: 'https://www.linkedin.com/in/michelleperezs/' },
  { label: 'Email', href: `mailto:${EMAIL}` },
  { label: 'CV', href: '/MichellePerezCV.pdf' },
];

const EXPERIENCE = [
  {
    date: '2021 — now',
    role: 'Software Engineer & Technical Instructor',
    org: 'Codelearn',
    text: 'Teaching programming at every level, from Scratch to Python, C++, SQL and JavaScript: logic, algorithms and 1:1 mentoring on problem-solving and good coding practices.',
  },
  {
    date: '2026',
    role: 'Software Developer Intern',
    org: 'Mimesis Lab',
    text: 'Angular 19 dashboard and NestJS + PostgreSQL API for an AI therapy-research platform, with role-based access and Whisper transcription data.',
  },
  {
    date: '2024 — 25',
    role: 'Software Developer Intern',
    org: 'DeepDesignSystems',
    text: 'Corporate website and Markdown-powered blog with Next.js, built from Figma designs.',
  },
];

const AWARDS = [
  ['2025', 'Featured speaker, Scratch Day Quito'],
  ['2020', '2nd place, Conecta Culturas Hackathon (UNESCO, DW Akademie)'],
  ['2020', '2nd place, Hackea la Crisis Mujeres + Niñas (UNICEF, UNDP)'],
  ['2020', '2nd place, Hackea Covid19 (MEDIALAB UIO, GIZ)'],
];

function SectionTitle({ children }) {
  return (
    <Heading
      as="h2"
      fontSize="xl"
      fontWeight="500"
      color="ink"
      display="inline-block"
      mb={6}
      textDecoration="underline"
      textDecorationColor="accent"
      textDecorationThickness="3px"
      textUnderlineOffset="7px"
    >
      {children}
    </Heading>
  );
}

function Section({ id, title, children }) {
  return (
    <Box as="section" id={id} mt={{ base: 16, md: 20 }} scrollMarginTop="80px">
      <SectionTitle>{title}</SectionTitle>
      {children}
    </Box>
  );
}

export default function Home() {
  const { hash } = useLocation();
  const projects = getAllProjects();

  useEffect(() => {
    if (hash) document.getElementById(hash.slice(1))?.scrollIntoView({ behavior: 'smooth' });
  }, [hash]);

  return (
    <>
      <Helmet>
        <title>Michelle Pérez — Fullstack Developer</title>
        <meta
          name="description"
          content="Michelle Pérez — fullstack developer in Manresa, Barcelona. Projects, write-ups and experience in Python, TypeScript, data and AI."
        />
        <link rel="canonical" href="https://michelle-perez.web.app/" />
      </Helmet>

      {/* ---------- Intro ---------- */}
      <Box as="section">
        <Heading as="h1" fontSize={{ base: '4xl', md: '5xl' }} fontWeight="500" color="ink" letterSpacing="-0.02em" lineHeight={1.1}>
          Michelle Pérez
        </Heading>
        <Text mt={2} color="inkDim">Fullstack developer · Manresa, Barcelona</Text>

        <Text mt={8} fontSize="lg" lineHeight={1.75} color="ink">
          I build web apps and data tools, and I&apos;ve been teaching programming since 2021 — so I like
          understanding how things work, and explaining it. Here you&apos;ll find what I&apos;ve built and
          how I built it.
        </Text>

        <HStack mt={6} spacing={2} fontSize="sm" color="inkDim">
          <Box w="8px" h="8px" borderRadius="full" bg="#22A06B" flexShrink={0} />
          <Text>Open to full-time roles and freelance projects</Text>
        </HStack>

        <Wrap mt={6} spacing={5} fontSize="sm">
          {LINKS.map((l) => (
            <Link
              key={l.label}
              href={l.href}
              isExternal={!l.href.startsWith('mailto')}
              color="accent"
              fontWeight="500"
              _hover={{ textDecoration: 'underline', textUnderlineOffset: '3px' }}
            >
              {l.label} ↗
            </Link>
          ))}
        </Wrap>
      </Box>

      {/* ---------- Projects ---------- */}
      <Section id="projects" title="Projects">
        <Box as="ul" listStyleType="none">
          {projects.map((p) => (
            <Box as="li" key={p.slug}>
              <Link
                as={RouterLink}
                to={`/projects/${p.slug}`}
                role="group"
                display="block"
                py={5}
                mx={-4}
                px={4}
                borderRadius="lg"
                _hover={{ textDecoration: 'none', bg: 'accentSoft' }}
              >
                <Flex justify="space-between" align="baseline" gap={4}>
                  <HStack spacing={3} align="baseline" wrap="wrap">
                    <Text color="ink" fontWeight="600" fontSize="lg" _groupHover={{ color: 'accent' }}>
                      {p.title}
                    </Text>
                    {p.status && (
                      <Text fontSize="xs" color="inkDim" border="1px solid" borderColor="border" borderRadius="full" px={2}>
                        {p.status}
                      </Text>
                    )}
                  </HStack>
                  <Text fontSize="sm" color="inkDim" flexShrink={0}>{p.date.slice(0, 4)}</Text>
                </Flex>
                <Text mt={1.5} lineHeight={1.7} color="inkDim">{p.summary}</Text>
                <Flex mt={3} justify="space-between" align="center" gap={4}>
                  <Wrap spacing={2}>{p.tags.map((t) => <Tag key={t}>{t}</Tag>)}</Wrap>
                  <HStack spacing={1} fontSize="sm" color="accent" fontWeight="500" flexShrink={0}
                    opacity={{ base: 1, md: 0 }} _groupHover={{ opacity: 1 }} transition="opacity .15s">
                    <Text>Read</Text>
                    <ArrowRight size={14} />
                  </HStack>
                </Flex>
              </Link>
            </Box>
          ))}
        </Box>
      </Section>

      {/* ---------- Experience ---------- */}
      <Section id="experience" title="Experience">
        <Box as="ul" listStyleType="none">
          {EXPERIENCE.map((e) => (
            <Grid as="li" key={e.org} templateColumns={{ base: '1fr', sm: '96px 1fr' }} gap={{ base: 1, sm: 4 }} py={4}>
              <Text fontSize="sm" color="inkDim" pt={0.5}>{e.date}</Text>
              <Box>
                <Text color="ink" fontWeight="500">
                  {e.role} <Box as="span" color="inkDim" fontWeight="400">· {e.org}</Box>
                </Text>
                <Text mt={1} fontSize="sm" lineHeight={1.7} color="inkDim">{e.text}</Text>
              </Box>
            </Grid>
          ))}
        </Box>
        <Text mt={4} fontSize="sm" color="inkDim">
          Full details in my CV:{' '}
          <Link href="/MichellePerezCV.pdf" isExternal color="accent" fontWeight="500">English</Link>
          {' · '}
          <Link href="/MichellePerezCV-ES.pdf" isExternal color="accent" fontWeight="500">Español</Link>
        </Text>
      </Section>

      {/* ---------- About ---------- */}
      <Section id="about" title="About me">
        <Flex gap={6} direction={{ base: 'column', sm: 'row' }} align={{ sm: 'flex-start' }}>
          <Image
            src="/michelleprofile.jpg"
            alt="Michelle Pérez"
            boxSize="96px"
            borderRadius="full"
            objectFit="cover"
            border="2px solid"
            borderColor="border"
            flexShrink={0}
          />
          <Box lineHeight={1.8}>
            <Text>
              I&apos;m originally from Ecuador and now live in Manresa. I&apos;m in the final year of my
              Computer Engineering degree at UNIR, and since 2021 I&apos;ve taught programming to students
              of every level — which has made me care a lot about clear code and clear explanations.
            </Text>
            <Text mt={4}>
              Most recently I built the dashboard and API of an AI platform for therapy research at Mimesis Lab, and
              analysing Ecuador&apos;s hospital data is one of my favourite projects. That&apos;s why I&apos;m
              looking towards <Box as="span" color="ink" fontWeight="500">bioinformatics</Box> next — where
              software, data and biology meet.
            </Text>
          </Box>
        </Flex>

        <Text mt={10} mb={3} fontSize="sm" fontWeight="600" color="ink">Awards & talks</Text>
        <Box as="ul" listStyleType="none" fontSize="sm">
          {AWARDS.map(([y, t]) => (
            <Grid as="li" key={t} templateColumns="56px 1fr" py={1.5}>
              <Text color="inkDim">{y}</Text>
              <Text>{t}</Text>
            </Grid>
          ))}
        </Box>

        <Text mt={8} mb={3} fontSize="sm" fontWeight="600" color="ink">Languages</Text>
        <Text fontSize="sm">Spanish and Catalan (native) · English (intermediate)</Text>
      </Section>

      {/* ---------- Contact ---------- */}
      <Box mt={{ base: 16, md: 20 }} p={{ base: 6, md: 8 }} borderRadius="xl" bg="accentSoft">
        <Text color="ink" fontSize="lg" fontWeight="500">Have a project or a role in mind?</Text>
        <Text mt={2} color="inkDim">
          Write to me at{' '}
          <Link href={`mailto:${EMAIL}`} color="accent" fontWeight="500">{EMAIL}</Link>
          {' '}— I&apos;d love to hear about it.
        </Text>
      </Box>
    </>
  );
}
